import { SiteIcon } from './SiteBrand';
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { VoiceTextField } from "./voice";
import { legacyFetch } from "./legacy-api";
import { ProjectPhoto, prepareImage, invalidatePresentation } from "./Presentation";
import { api } from "./api";
import type { User } from "@revival/contracts";

type ProjectSummary = {
  id: string;
  name: string;
  phase: string;
  address: string;
  jobReference: string;
  totalSnags?: number;
  openSnags?: number;
  reviewSnags?: number;
  closedSnags?: number;
};

export default function ProjectsHome({
  projects,
  activeProjectId,
  onOpen,
  onOpenSnags,
  onAddSnag,
  onSetup,
  onArchive,
  onRefresh,
  newProjectOpen,
  onNewProjectOpenChange,
  newProjectRequest,
  isAdministrator,
  offline,
}: {
  projects: ProjectSummary[];
  activeProjectId: string;
  onOpen: (projectId: string) => Promise<void>;
  onOpenSnags: (projectId: string, filter: "active" | "ready_for_inspection" | "closed") => Promise<void>;
  onAddSnag: () => void;
  onSetup: (projectId: string) => Promise<void>;
  onArchive: (project: ProjectSummary) => Promise<void>;
  onRefresh: () => Promise<void>;
  newProjectOpen: boolean;
  onNewProjectOpenChange: (open: boolean) => void;
  newProjectRequest: number;
  isAdministrator: boolean;
  offline: boolean;
}) {
  const newProjectPanelRef = useRef<HTMLElement>(null);
  const [name, setName] = useState("");
  const [phase, setPhase] = useState("");
  const [address, setAddress] = useState("");
  const [jobReference, setJobReference] = useState("");
  const [managerAccess, setManagerAccess] = useState<"admin" | "all" | "selected">("admin");
  const [managerIds, setManagerIds] = useState<string[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [createdProjectId, setCreatedProjectId] = useState("");
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const creating = useRef(false);
  const photoInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!photo) { setPhotoPreview(""); return; }
    const url = URL.createObjectURL(photo); setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  useEffect(() => {
    if (!newProjectOpen || newProjectRequest === 0) return;
    const frame = window.requestAnimationFrame(() => {
      newProjectPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [newProjectOpen, newProjectRequest]);

  useEffect(() => {
    if (!isAdministrator) return;
    let active = true;
    void api.users().then((items) => { if (active) setManagers(items.filter((item) => item.role === "manager" && item.active)); }).catch(() => undefined);
    return () => { active = false; };
  }, [isAdministrator]);

  const visibleProjects = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return projects;
    return projects.filter((project) => `${project.name} ${project.phase} ${project.address}`.toLowerCase().includes(query));
  }, [projects, search]);

  const totals = useMemo(() => projects.reduce((result, project) => ({
    open: result.open + (project.openSnags ?? 0),
    review: result.review + (project.reviewSnags ?? 0),
  }), { open: 0, review: 0 }), [projects]);

  async function finishProject(projectId: string) {
    await onRefresh();
    await onSetup(projectId);
    setName(""); setPhase(""); setAddress(""); setJobReference(""); setManagerAccess("admin"); setManagerIds([]);
    setPhoto(null); setCreatedProjectId(""); setPhotoUploaded(false);
    if (photoInput.current) photoInput.current.value = "";
    onNewProjectOpenChange(false);
    setMessage("");
  }

  async function createProject(skipPhoto = false) {
    if (offline) { setMessage("Connect to create a new project. Your saved projects are available offline."); return; }
    if (creating.current) return;
    if (!createdProjectId && !name.trim()) { setMessage("Add a project name first"); return; }
    creating.current = true; setSaving(true); setMessage("");
    let savedId = createdProjectId;
    try {
      // Validate/resize before creating anything. Retain the created ID if upload/refresh fails.
      const prepared = photo && !skipPhoto && !photoUploaded ? await prepareImage(photo) : null;
      if (!savedId) {
        const response = await legacyFetch("/api/manager/projects", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, phase, address, jobReference, managerAccessAll: managerAccess === "all", managerIds: managerAccess === "selected" ? managerIds : [] }),
        });
        const result = await response.json() as { id: string; error?: string };
        if (!response.ok) throw new Error(result.error || "Could not create this project");
        savedId = result.id; setCreatedProjectId(savedId);
      }
      if (prepared) { await api.uploadPresentation("project", savedId, prepared); invalidatePresentation("project", savedId); setPhotoUploaded(true); }
      await finishProject(savedId);
    } catch (error) {
      const reason = error instanceof Error ? error.message : "Please try again.";
      setMessage(savedId ? `The project has been created. ${reason} Retry to finish, or continue without uploading the photo.` : reason);
    } finally { creating.current = false; setSaving(false); }
  }

  return <section className="projects-home">
    <div className="projects-title-row">
      <div><p className="eyebrow">MANAGER HOME</p><h1>Projects</h1><p>{offline ? "Open a saved project. New projects need a connection." : "Open a project to see its snags and progress."}</p></div>
      <button aria-label="Add snag" className="new-project-button" disabled={saving} onClick={onAddSnag} type="button"><SiteIcon name="plus" /><span>Add snag</span></button>
    </div>

    <div className="projects-overview-strip">
      <div><strong>{projects.length}</strong><span>Running projects</span></div>
      <div><strong>{totals.open}</strong><span>Open snags</span></div>
      <div><strong>{totals.review}</strong><span>Ready to inspect</span></div>
    </div>

    {newProjectOpen && !offline && <article className="new-project-panel" ref={newProjectPanelRef}>
      <header><div><span>＋</span></div><section><h2>Create a new project</h2><p>Speak or type the basic details. Rooms and contractors can be added afterwards.</p></section><button aria-label="Close new project form" disabled={saving} onClick={() => onNewProjectOpenChange(false)} type="button">×</button></header>
      <fieldset className="new-project-fields" disabled={saving || Boolean(createdProjectId)}>
        <VoiceTextField label="Project name" value={name} onChange={setName} placeholder="Example: Abbey Junior School" />
        <VoiceTextField label="Job reference" value={jobReference} onChange={setJobReference} placeholder="Example: RH-1042" />
        <VoiceTextField label="Phase or work package" value={phase} onChange={setPhase} placeholder="Example: Summer refurbishment" />
        <VoiceTextField label="Site address" value={address} onChange={setAddress} placeholder="Speak or type the address" />
        <label className="project-photo-field">Project photo <small>Optional</small>
          <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { setPhoto(event.target.files?.[0] ?? null); setPhotoUploaded(false); }} />
          {photoPreview && <img src={photoPreview} alt="Selected project photo" />}
          {photo && <button type="button" onClick={() => { setPhoto(null); if (photoInput.current) photoInput.current.value = ""; }}>Remove photo</button>}
        </label>
      </fieldset>
      {isAdministrator && <fieldset disabled={saving || Boolean(createdProjectId)} className="project-manager-access"><label>Site Manager access<select value={managerAccess} onChange={(event) => setManagerAccess(event.target.value as typeof managerAccess)}><option value="admin">Administrator only for now</option><option value="all">Share with all Site Managers</option><option value="selected">Choose Site Managers</option></select></label>{managerAccess === "selected" && <div>{managers.map((manager) => <label key={manager.id}><input checked={managerIds.includes(manager.id)} onChange={(event) => setManagerIds((current) => event.target.checked ? [...current, manager.id] : current.filter((id) => id !== manager.id))} type="checkbox" /> {manager.name}</label>)}{!managers.length && <small>No active Site Managers have been registered yet.</small>}</div>}</fieldset>}
      <button className="create-project-submit" disabled={saving || (!createdProjectId && !name.trim())} onClick={() => void createProject()} type="button">{saving ? "Saving…" : createdProjectId ? "Retry to finish" : "Create project"}</button>
      {createdProjectId && <button className="create-project-skip-photo" disabled={saving} onClick={() => void createProject(true)} type="button">Continue without uploading photo</button>}
    </article>}

    {message && <div className="project-message">{message}</div>}

    <div className="projects-list-heading">
      <div><h2>Current running projects</h2><span>{visibleProjects.length} shown</span></div>
      <div className="projects-heading-tools"><label><span>⌕</span><input aria-label="Search projects" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search projects" /></label></div>
    </div>

    <div className="project-card-grid compact-project-list">
      {visibleProjects.map((project) => {
        const total = project.totalSnags ?? 0;
        const closed = project.closedSnags ?? 0;
        const progress = total ? Math.round((closed / total) * 100) : 0;
        const active = project.id === activeProjectId;
        return <article aria-label={`Open ${project.name}`} className={active ? "project-card active" : "project-card"} key={project.id} onClick={(event) => { if ((event.target as HTMLElement).closest("button,summary,input,select")) return; void onOpen(project.id); }} onKeyDown={(event) => { if ((event.key === "Enter" || event.key === " ") && event.target === event.currentTarget) { event.preventDefault(); void onOpen(project.id); } }} role="button" tabIndex={0}>
          <ProjectPhoto projectId={project.id} name={project.name} />
          <div className="project-card-body"><div className="project-card-labels"><span className="running-chip">Running</span>{active && <span className="current-chip">Current</span>}</div><button className="project-name-button" onClick={() => void onOpen(project.id)} type="button"><h3>{project.name}</h3></button>{project.phase && <p className="project-phase">{project.phase}</p>}{project.jobReference && <p className="project-reference">{project.jobReference}</p>}<div className="project-progress-row"><strong>{progress}% complete</strong></div><div className="project-progress-track"><span style={{width: `${progress}%`}} /></div></div>
          <button className="project-open-chevron" aria-label={`Open project ${project.name}`} onClick={() => void onOpen(project.id)} type="button"><SiteIcon name="next" /></button>
          <div className="project-card-counts">
            <button aria-label={`View ${project.openSnags ?? 0} open snags for ${project.name}`} onClick={() => void onOpenSnags(project.id, "active")} type="button"><strong>{project.openSnags ?? 0}</strong><span>Open</span></button>
            <button aria-label={`View ${project.reviewSnags ?? 0} snags awaiting inspection for ${project.name}`} onClick={() => void onOpenSnags(project.id, "ready_for_inspection")} type="button"><strong>{project.reviewSnags ?? 0}</strong><span>Inspection</span></button>
            <button aria-label={`View ${closed} closed snags for ${project.name}`} onClick={() => void onOpenSnags(project.id, "closed")} type="button"><strong>{closed}</strong><span>Closed</span></button>
          </div>
          <details className="project-manage"><summary>Project details & setup</summary><p>{project.address || "Address not added"}</p><div><button className="project-setup-button" disabled={offline} title={offline ? "Connect to edit project setup" : undefined} onClick={() => void onSetup(project.id)} type="button">Set up</button><button className="project-archive-button" disabled={offline} title={offline ? "Connect to archive a project" : undefined} onClick={() => void onArchive(project)} type="button">Archive</button><button className="project-open-button" onClick={() => void onOpen(project.id)} type="button">Open project</button></div></details>
        </article>;
      })}
    </div>
    {!visibleProjects.length && <div className="empty-projects"><span>⌕</span><strong>No projects found</strong><p>Try a different search or add a new project.</p></div>}
  </section>;
}
